import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  STATUS_META,
  type ChangeEntry,
  type DsEntry,
} from '@/designs-dev/inspect/ds-registry';
import { ComponentNameLink } from '@/designs-dev/inspect/ComponentNameLink';

type Row = ChangeEntry & { component?: string; demo?: DsEntry['demo'] };

// One changelog table for every surface that lists changes: the compiled
// Design system changelog (with the component column) and the per-component
// changelogs in the registry docs and inspect panel (without it).

// The Status / Date / Change cells, shared by both the flat per-component table
// and the grouped Design-system changelog.
const EntryCells = ({
  row,
  groupStart,
}: {
  row: Row;
  groupStart?: boolean;
}) => {
  const cell = {
    verticalAlign: 'top',
    ...(groupStart && { borderTop: 2, borderTopColor: 'divider' }),
  } as const;
  return (
    <>
      <TableCell sx={cell}>
        <Chip
          size='small'
          label={STATUS_META[row.status].label}
          color={STATUS_META[row.status].color}
        />
      </TableCell>
      <TableCell sx={cell}>
        <Typography variant='body2' color='text.secondary'>
          {row.date}
        </Typography>
      </TableCell>
      <TableCell sx={cell}>
        <Typography variant='body2'>{row.note}</Typography>
      </TableCell>
    </>
  );
};

// Groups rows by component, preserving the incoming (date-sorted) order so each
// group sits where its newest entry would — keeping the table newest-first while
// nesting same-component changes together.
const groupByComponent = (rows: Row[]) => {
  const groups: { component: string; entries: Row[] }[] = [];
  const seen = new Map<string, number>();
  for (const row of rows) {
    const key = row.component ?? '';
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ component: key, entries: [] });
    }
    groups[seen.get(key)!].entries.push(row);
  }
  return groups;
};

const ChangelogTable = ({ rows }: { rows: Row[] }) => {
  const showComponent = rows.some((r) => r.component);

  if (!showComponent) {
    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell width={96}>Status</TableCell>
            <TableCell width={104}>Date</TableCell>
            <TableCell>Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c, i) => (
            <TableRow key={i}>
              <EntryCells row={c} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // Grouped Design-system changelog: Component leads each group once, with its
  // changes nested beneath via a row-spanning cell. The trailing "In action"
  // cell is also row-spanned — one deep-link per component, not per change.
  const groups = groupByComponent(rows);
  const groupCell = {
    verticalAlign: 'top',
    borderTop: 2,
    borderTopColor: 'divider',
  } as const;
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell width='24%'>Component</TableCell>
          <TableCell width={96}>Status</TableCell>
          <TableCell width={104}>Date</TableCell>
          <TableCell>Change</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map((group) =>
          group.entries.map((c, i) => (
            <TableRow key={`${group.component}-${i}`}>
              {i === 0 && (
                <TableCell rowSpan={group.entries.length} sx={groupCell}>
                  <ComponentNameLink name={group.component} />
                </TableCell>
              )}
              <EntryCells row={c} groupStart={i === 0} />
            </TableRow>
          )),
        )}
      </TableBody>
    </Table>
  );
};

export default ChangelogTable;
